import moment from 'moment'
// Explicit, because this module calls moment.tz(). It worked only because other components
// (BookingEvents, AlertsCoundown, ...) happen to import moment-timezone first and patch the
// shared moment singleton — so getFlightDuration's timezone branch was one import-order
// change away from throwing. Importing it here makes the dependency real.
import 'moment-timezone'
import { connectedApiService as api } from '../../index'

export const getFormattedTime = date => {
  let mTime = moment.utc(date).format('YYYY-MM-DD HH:mm')
  let weekDay = moment(mTime).format('ddd')
  let day = moment(mTime).format('D')
  let month = moment(mTime).format('MMM')
  let hours = moment(mTime).hour()
  let minutes = moment(mTime).minutes()

  const hoursWithZero = ('0' + hours).slice(-2)
  const minutesWithZero = ('0' + minutes).slice(-2)
  const dayWithZero = ('0' + day).slice(-2)

  const dayInfo = {
    monthDay: dayWithZero,
    weekDay: weekDay,
    month: month,
  }
  const timeInfo = {
    hours: hoursWithZero,
    minutes: minutes,
    scheduleMinutes: minutesWithZero,
  }
  return { day: dayInfo, time: timeInfo }
}

export const occurrenceDate = async date => {
  const serverTime = moment.utc()
  const flightTime = moment.utc(date)
  const result = moment(flightTime).isAfter(serverTime)

  //postive, negative, or zero
  //today and yesterday both have the same thing you pass the date, and get the flight information
  if (!result) {
    return 'today-yesterday'
  } else if (result < 0 || result) {
    return 'tomorrow'
  }
}

export const getTimeDifference = (arriveTime, departureTime) => {
  let arriveMoment = moment.utc(arriveTime)
  let departureMoment = moment.utc(departureTime)
  let diff = arriveMoment.diff(departureMoment)

  return diff
}

// Elapsed flight time. Three sources, most trustworthy first.
//
// 1. The UTC timestamps FlightNerve returns alongside the local ones. These are absolute
//    instants, so the subtraction is correct whatever the two airports' offsets are, and —
//    critically — it still works when the timezone lookup comes back empty.
// 2. Both timezones known: convert the local readings properly.
// 3. Neither: subtract the local readings as if they were UTC.
//
// Branch 3 is a guess, and it was silently producing wrong numbers on real bookings.
// FlightNerve has NO timezone for DOH, KWI or IST (`/airport?code=` returns
// "timezone": null), and for those airports it also stamps the flight with a +00:00
// offset, so `scheduledTime === scheduledTimeUtc`. QR1002 DOH->DXB therefore arrived here
// as departure "22:05" and arrival "03:25" and branch 3 reported 5h 20m — for a hop
// FlightNerve's own `duration` field calls "1 hr 20 min", and whose UTC pair
// (22:05Z -> 23:25Z) gives exactly that. Branch 1 fixes DOH/KWI/IST and any airport they
// lose next, without needing to know the offset at all.
//
// Kept as a fallback CHAIN rather than a replacement: `utc` is absent on the cached and
// legacy payload shapes, and a wrong-but-plausible duration still beats a blank card.
export const getFlightDuration = (arrival, departure) => {
  let arvTime = null
  let depTime = null
  let totalTime = null

  if (arrival.utc && departure.utc) {
    arvTime = moment.utc(arrival.utc)
    depTime = moment.utc(departure.utc)
    // Guarded: a malformed or transposed pair would render as a negative or absurd
    // duration, which is worse than the imperfect fallbacks below. A scheduled flight is
    // never zero-length and never longer than a day.
    if (arvTime.isValid() && depTime.isValid()) {
      const utcDiff = arvTime.diff(depTime)
      if (utcDiff > 0 && utcDiff < 24 * 60 * 60 * 1000) {
        totalTime = utcDiff
      }
    }
  }

  if (totalTime === null) {
    if (arrival.timezone && departure.timezone) {
      arvTime = moment.tz(arrival.time, 'YYYYMMDD HH:mm:ss', arrival.timezone)
      depTime = moment.tz(
        departure.time,
        'YYYYMMDD HH:mm:ss',
        departure.timezone
      )
      totalTime = arvTime.diff(depTime)
    } else {
      arvTime = moment.utc(arrival.time)
      depTime = moment.utc(departure.time)
      totalTime = arvTime.diff(depTime)
    }
  }

  let timeToString = moment.utc(totalTime).format('H:mm')
  let [hours, minutes] = timeToString.split(':')

  return `${hours}h ${minutes}m`
}

export const getCurrentDuration = departure => {
  const serverTime = moment.utc()
  const depTime = moment.utc(departure)
  const timeDiff = serverTime.diff(depTime)

  let timeToString = moment.utc(timeDiff).format('H:mm')
  let [hours, minutes] = timeToString.split(':')

  return `${hours}h ${minutes}m`
}
export const getProgressTime = (arrivalTime, departureTime, status) => {
  //re-do if something happens
  //flight status landed == arrival-departure (progress Time)
  //flight status active == serverTime-departure (progress Time)
  //flight status schedulated == serverTime-departure (progress Time)

  if (status === 'Arrived') {
    return 100
  } else if (status === 'In Air') {
    const arrivalTimeMoment = moment.utc(arrivalTime)
    const departureTimeMoment = moment.utc(departureTime)
    const serverTimeMoment = moment.utc()
    const totalFlightTime = arrivalTimeMoment.diff(departureTimeMoment)
    const progressTime = serverTimeMoment.diff(departureTimeMoment)
    const totalTimeInMinutes = Math.floor(totalFlightTime / 1000 / 60)
    const progressTimeInMinutes = Math.floor(progressTime / 1000 / 60)
    const progressTimePercentage = Math.floor(
      (progressTimeInMinutes / totalTimeInMinutes) * 100
    )
    return progressTimePercentage
  } else {
    return 0.1
  }
  // console.log("arrival", arrivalTime)
  // console.log("departure", departureTime)
  // console.log("status", status)
  // const arrivalTimeMoment = moment(arrivalTime)
  // const departureTimeMoment = moment(departureTime)
  // const totalFlightTime = arrivalTimeMoment.diff(departureTimeMoment)
  // const serverTimeMoment = moment.utc()
  // const progressTime = serverTimeMoment.diff(departureTimeMoment)

  // if (progressTime > 0) {
  //   const totalTimeInMinutes = Math.floor(totalFlightTime / 1000 / 60)
  //   const progressTimeInMinutes = Math.floor(progressTime / 1000 / 60)
  //   const progressTimePercentage = Math.floor(
  //     (progressTimeInMinutes / totalTimeInMinutes) * 100
  //   )
  //   // return 30
  //   console.log("progressTimePercentage", progressTimePercentage)
  //   return progressTimePercentage
  // } else {
  //   return 0.1
  // }
}

export const getAirlineName = async iata => {
  const { data } = await api.get(`airline/flight/${iata}`)

  return data
}

// ── Timezone fallback: TimeZoneDB by position ───────────────────────────────
// This fills a CONTRACT GAP, not a special case. FlightNerve documents `timezone` as a
// normal field on /airport (their own example shows `"timezone": "Asia/Dubai"`, and it is
// not marked optional or nullable), so every caller here is entitled to one. When they
// return null they are simply not holding up their end, and the rest of this module has no
// way to do its job.
//
// Measured 2026-08-10: swept 120 airports, three come back null — DOH, KWI and IST. Every
// other airport in our logs is populated. Those three records still carry
// latitude/longitude, so a position lookup always has what it needs. Verified against
// TimeZoneDB: Asia/Qatar, Asia/Kuwait, Europe/Istanbul, all correct, and it handles the
// awkward cases too (Kathmandu +5:45, Adelaide +9:30, JFK across a DST boundary).
//
// TimeZoneDB is already this app's answer for exactly this question: `getTimeZoneId` in
// NewBookingForm/constants.js plus roughly six inline copies of the same URL. Not imported
// from there deliberately — a shared component must not depend on a page module — and the
// key matches those existing call sites rather than introducing a new one.
//
// Self-disabling and close to free: only reached when FlightNerve returns no timezone, so
// it stops firing the moment they populate the field, and the whole lookup sits behind
// getAirlineTimeZoneCached's per-pair session cache.
//
// NOTE on reach: the only consumer of the resolved zone today is getFlightDuration's
// second branch, which runs when the UTC pair is unavailable (cached and legacy payload
// shapes). Correcting the DISPLAYED local time and date needs a further step — recomputing
// them from the UTC instant plus this zone instead of trusting FlightNerve's
// `scheduledTime` string. This is the prerequisite for that, not a substitute for it.
const TIMEZONEDB_KEY = '7QN5B24ESJOB'

const resolveZoneByPosition = async airport => {
  const lat = airport && airport.latitude
  const lng = airport && airport.longitude
  if (typeof lat !== 'number' || typeof lng !== 'number') return null
  try {
    const response = await fetch(
      `https://vip.timezonedb.com/v2.1/get-time-zone?key=${TIMEZONEDB_KEY}&by=position&lat=${lat}&lng=${lng}&format=json`
    )
    if (!response.ok) return null
    const details = await response.json()
    return details && details.status === 'OK' ? details.zoneName || null : null
  } catch (error) {
    // A missing timezone is degraded, not fatal — getFlightDuration has its own
    // fallbacks. Never let this break a lookup that otherwise succeeded.
    console.log('resolveZoneByPosition error', error)
    return null
  }
}

const resolveAirportZone = async airportResponse => {
  const airport = airportResponse && airportResponse.data
  if (!airport) return null
  return airport.timezone || (await resolveZoneByPosition(airport))
}

// ── True local wall-clock, rebuilt from the instant ─────────────────────────
// FlightNerve's local strings are only as good as the offset it applied, and for an
// airport it has no timezone for it applies +00:00 — so the "local" reading is really UTC
// (DOH/KWI/IST, see resolveZoneByPosition above). Rebuild the wall-clock from the true
// instant and the zone we resolved.
//
// A NO-OP wherever FlightNerve is already right: MAD's 15:45+02:00 is instant 13:45Z, and
// 13:45Z in Europe/Madrid is 15:45 again. It only moves the airports that were wrong.
//
// Emitted with a trailing Z — the wall-clock LABELLED as UTC, which is the convention every
// consumer of `estimated`/`scheduled` in this module already expects (resolveFlightDateTimes
// produces exactly that shape via moment.utc(naive).format()).
//
// This function first shipped emitting a NAIVE "YYYY-MM-DDTHH:mm:ss", reasoning that
// getFormattedTime reads through moment.utc() and would render it verbatim. That much is
// true, but it is not the only consumer: processData reassigns
// `arrival.estimated = moment.utc(new Date(arrival.estimated)).format()` in its
// cross-midnight branches, and `new Date()` parses an offset-less date-time in the BROWSER's
// timezone before that converts it back to UTC. The value therefore moved by whatever offset
// the viewer happened to be in — a flight landing 13:45 in Athens produced a 10:45 pick-up
// for a UTC+3 viewer and 12:45 for a UTC+1 viewer, on the same booking. Same-value-different-
// machine is the signature; it reached production and mis-set pick-up times on live bookings.
//
// The Z removes the whole class rather than patching those two call sites: it is correct
// under moment.utc() AND under new Date(), so no present or future consumer can reinterpret
// it against local time.
export const localWallClockFromUtc = (utcInstant, timezone) => {
  if (!utcInstant || !timezone) return null
  const instant = moment(utcInstant)
  if (!instant.isValid()) return null
  const local = instant.tz(timezone)
  return local && local.isValid()
    ? local.format('YYYY-MM-DDTHH:mm:ss[Z]')
    : null
}

// Repointed to flightNerve/airport (Phase 0) — one call per IATA code,
// since FlightNerve's airport endpoint is per-airport, not per-route-pair
// like the old custom backend route. Preserves the exact array-wrapped
// [{arrival_timeZone, departure_timeZone}] shape resolveFlightMeta in
// FlightTracking.jsx already destructures via `const [obj] = airlineTimeZone`.
// One request per airport code for the page's lifetime: the timezone lookup below and the
// itinerary-mismatch banner's airport name (FlightAirportLabel) read the same record. A failed
// request is dropped so it can be retried.
const airportResponseCache = new Map()
export const getAirportResponseCached = code => {
  if (!code) return Promise.resolve(null)
  if (!airportResponseCache.has(code)) {
    airportResponseCache.set(
      code,
      api
        .get(`flightNerve/airport?code=${encodeURIComponent(code)}`, false)
        .catch(() => {
          airportResponseCache.delete(code)
          return null
        })
    )
  }
  return airportResponseCache.get(code)
}

export const getAirlineTimeZone = async (arr_iata, dep_iata) => {
  // Never ask the backend about an EMPTY airport code — a payload with a missing
  // endpoint (projected instances can carry one) produced literal
  // `GET /flightNerve/airport?code=` 400s. A blank side simply yields no timezone.
  const fetchTz = code => getAirportResponseCached(code)
  const [arrivalAirport, departureAirport] = await Promise.all([
    fetchTz(arr_iata),
    fetchTz(dep_iata),
  ])
  const [arrivalZone, departureZone] = await Promise.all([
    resolveAirportZone(arrivalAirport),
    resolveAirportZone(departureAirport),
  ])
  return [
    {
      arrival_timeZone: arrivalZone,
      departure_timeZone: departureZone,
    },
  ]
}

// Google-places airport entries often carry NO IATA code ("Nantucket Memorial
// Airport" has no "(ACK)" in its text), which starves the resolver's
// expectedArrival/expectedDeparture hints and lets a multi-leg number resolve to
// the wrong leg. Resolve the code from our own cached backend (30d TTL, coalesced).
export const resolveAirportIata = async airportName => {
  try {
    const name = String(airportName || '')
      .split(',')[0]
      .trim()
    if (!name) return null
    const { data } = await api.get(
      `flightNerve/airport?search=${encodeURIComponent(name)}`,
      false
    )
    const first = Array.isArray(data) ? data[0] : data
    const iata = first && first.iata
    return /^[A-Z]{3}$/.test(String(iata || '')) ? iata : null
  } catch (e) {
    return null
  }
}

export const getAirportIataFromName = async (arrival, departure) => {
  const { data } = await api.get(`airport/flight/${arrival}/${departure}`)

  return data
}

export const getAirportIata = async ({ lng, lat }) => {
  const { data } = await api.get(`booking/airport/${lng}/${lat}`)

  return data.iata
}
export const getFutureDate = (
  pickUpDate,
  dropOffDate,
  dropOffAtoB,
  flightInfo
) => {
  let dayDiff
  if (pickUpDate) {
    const pickDate = moment(pickUpDate)
    const arrivalObjectDate = moment(flightInfo.arrival.estimated).format(
      'YYYY-MM-DD'
    )
    dayDiff = pickDate.diff(arrivalObjectDate, 'days')
  } else if (dropOffAtoB) {
    const dropAtoBDate = moment(dropOffAtoB)
    const arrivalObjectDate = moment(flightInfo.departure.estimated).format(
      'YYYY-MM-DD'
    )
    dayDiff = dropAtoBDate.diff(arrivalObjectDate, 'days')
  } else if (dropOffDate) {
    const dropDate = moment(dropOffDate)
    const arrivalObjectDate = moment(flightInfo.arrival.estimated).format(
      'YYYY-MM-DD'
    )
    dayDiff = dropDate.diff(arrivalObjectDate, 'days')
  }
  const arrivalDate = moment
    .utc(flightInfo.arrival.scheduled)
    .add(dayDiff, 'days')

  const flightDuration = getTimeDifference(
    flightInfo.arrival.scheduled,
    flightInfo.departure.scheduled
  )
  const durationHours = moment.utc(flightDuration).format('HH:mm:ss')
  const arrivalTimeMoment = moment.utc(arrivalDate)
  const timeDifferenceMoment = moment.duration(durationHours)
  const departureDate = arrivalTimeMoment.subtract(timeDifferenceMoment)
  const date = {
    arrival: moment(arrivalDate).format('DD/MM/YYYY'),
    departure: moment(departureDate).format('DD/MM/YYYY'),
  }

  return date
}

// Re-exported from ./flightNervePure so callers that must stay free of the
// connectedApiService import (flightNerveResolver) can use them directly.
export {
  localCalendarDate,
  localDayFromScheduledTime,
  legLocalDay,
  normalizeIntlForAirportMatch,
  addressMatchesFlightAirportInfo,
  airportMatchesFlight,
  isCompleteFlightNumber,
  isValidFlightNumberFormat,
  canonicalFlightKey,
  ICAO_TO_IATA,
  parseFlightNumber,
  flightTimeMatchesBooking,
} from './flightNervePure'

// Who was at the keyboard. The channel could show the same flight failing twenty times
// without telling anyone whether that was twenty clients or one agent retyping — which is
// the question actually being asked when these come up.
//
// Carried INSIDE Reason rather than as its own `clientId` field, for the same reason the
// service label is: the Slack formatter (demo.rolzo.com/pipedrive) renders a fixed set of
// fields and echoes Reason verbatim, so a new top-level field would need a change over
// there, while this needs none. Named fields can be added later without touching this —
// see the note in the reporter.
//
// A clickable `user` link, so an entry is one click from whoever hit it. `<url|label>` is
// Slack's mrkdwn link syntax — the same mrkdwn the formatter already relies on for the
// bold field labels in these messages.
//
// The user page is /clients/:clientId/user/:userId (verified in
// router/routes/App/groups/systemRoutes.js), so it needs BOTH ids — hence the guard. The
// origin is read from the page rather than hardcoded: this only ever runs on
// business.rolzo.com because of the gate below, and reading it keeps the two in step.
//
// Never the email or the name: the link is enough to identify them, which leaves the
// channel free of personal data.
//
// Masquerade is the one extra word worth carrying: it means the person typing is one of
// OURS, not the client, so an internal test session is not read as a customer in trouble.
function describeReportingClient() {
  try {
    const auth = api.authInfo || {}
    const parts = []

    if (auth.companyId && auth.userId) {
      parts.push(
        `<${window.location.origin}/clients/${auth.companyId}/user/${auth.userId}|user>`
      )
    } else if (auth.userId) {
      // No company id means there is no user page to open — the id still identifies them.
      parts.push(`user ${auth.userId}`)
    }
    if (auth.isMasqueradeUser) parts.push('via admin')

    return parts.length ? parts.join(' · ') : null
  } catch (error) {
    // Identity is a nice-to-have; never let it cost us the report itself.
    return null
  }
}

export function flightErrorLog(data) {
  let allowedDomains = ['https://business.rolzo.com']

  if (!allowedDomains.includes(window.location.origin)) {
    return
  }

  const client = describeReportingClient()
  const payload = client ? { ...data, log: `${data.log} · ${client}` } : data

  fetch('https://demo.rolzo.com/pipedrive/flightErrorLog', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(console.error)
}
